"use client";

import React, { Fragment, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Image from "@/helpers/Image";
import Link from "next/link";
import { IHeader, IHeaderColumn } from "@/sanity/lib/definitions";
import LiveStreamPill from "@/components/global/livestream/LiveStreamPill";
import { cn } from "@/lib/utils";
import { IPageLink, resolveLink, resolvePageLink } from "@/sanity/globals/CTA";
import { createPortal } from "react-dom";
import { useGlobalPathname } from "@/components/global/partials/HeaderContext";
import { usePathname } from "next/navigation";
import {
  Tooltip,
  TooltipCustomContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/ui/tooltip";
import { CTAButton } from "@/shadcn/ui/button";
import { ArrowLeft, ChevronDown, ChevronRight } from "lucide-react";
import DecorationLine from "@/components/global/ui/DecorationLine";
import GlareHover from "../ui/GlareHover";
import IQCLogo from "../ui/IQCLogo";
import {
  DEFAULT_LANGUAGE,
  II18nText,
  resolveI18nText,
} from "@/sanity/globals/I18n";
import LanguageSwitcher from "./LanguageSwitcher";

import linkHoverBg from "@/public/global/headerLinkHoverbg.png";

const ease = [0.25, 0.1, 0.25, 1] as const;

const IconMobileMenuOpen = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="27"
    height="27"
    viewBox="0 0 27 27"
    fill="none"
  >
    <g opacity="0.5">
      <rect
        y="24.7485"
        width="35"
        height="2"
        transform="rotate(-45 0 24.7485)"
        fill="black"
      />
      <rect
        x="2.12109"
        width="35"
        height="2"
        transform="rotate(45 2.12109 0)"
        fill="black"
      />
    </g>
  </svg>
);
const IconMobileMenuCLosed = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="18"
    viewBox="0 0 32 18"
    fill="none"
    className="w-[4rem]"
  >
    <g opacity="0.5">
      <rect width="32" height="2" fill="black" />
      <rect y="8" width="32" height="2" fill="black" />
      <rect y="16" width="32" height="2" fill="black" />
    </g>
  </svg>
);

const TooltipLink = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn("cursor-pointer select-none group", className)}
    ref={ref}
    {...props}
  />
));
TooltipLink.displayName = "TooltipLink";

function MenuItemPopover({
  item,
  currentPath,
  headerStyle,
  language,
}: {
  item: {
    title: string;
    columns?: IHeaderColumn[];
  };
  currentPath: string;
  headerStyle: "light" | "dark";
  language?: string;
}) {
  const pathname = usePathname();

  return (
    <div className="flex justify-center w-screen h-fit">
      <div className="flex w-full h-full gap-[4rem] max-w-[160.5rem] pt-[3rem] pb-[5rem]">
        {(item.columns ?? []).map((col, colIndex) => {
          if (col.columnType === "none") return null;
          const resolvedColumnTitle = resolveI18nText(
            col.columnTitle,
            col.columnTitleI18n,
            language,
          );

          return (
            <motion.div
              key={col._key ?? colIndex}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: colIndex * 0.06,
                ease: ease,
              }}
              className={cn(
                "w-[min(48.75rem,100%)] h-full shrink-0",
                colIndex === 2 && "w-full flex-1",
                col.columnType === "imageSubItems" && "w-[min(74.125rem,100%)]",
              )}
            >
              {resolvedColumnTitle && (
                <>
                  <h5 className="uppercase !font-geist text-black/40 text-[1.75rem] font-[400]">
                    {resolvedColumnTitle}
                  </h5>
                  <hr className="border-t border-black/10 mt-[2rem] mb-[2rem]" />
                </>
              )}

              {/* subItems */}
              {col.columnType === "subItems" && (
                <div className="grid grid-cols-1">
                  {col.subItems &&
                    col.subItems.map((item, index) => {
                      const link = resolvePageLink(item.link, language) ?? "/";
                      const isActive = pathname === link;
                      const resolvedTitle = resolveI18nText(
                        item.title,
                        item.i18n,
                        language,
                      );
                      const resolvedDescription = resolveI18nText(
                        item.description,
                        item.descriptionI18n,
                        language,
                      );
                      return (
                        <Link
                          key={index}
                          href={link}
                          target={item.link.externalLink ? "_blank" : "_self"}
                          className={cn(
                            "group relative border-l border-transparent transition-all duration-500  ease-[cubic-bezier(0.25,0.1,0.25,1)]",
                            isActive
                              ? "pointer-events-none border-[#228E71]"
                              : "hover:border-[#228E71]",
                          )}
                          aria-current={isActive ? "page" : undefined}
                        >
                          <div
                            className={cn(
                              "flex items-start transition-all duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1)] gap-[1rem] p-[2rem] rounded-[.5rem]",
                              isActive ? "bg-white" : "hover:bg-[#F2F5F4]",
                            )}
                          >
                            <Image
                              src={linkHoverBg}
                              alt=""
                              className={cn(
                                "absolute right-0 top-0 !h-full !w-[80%] object-cover mix-blend-difference opacity-0",
                                isActive
                                  ? "opacity-100"
                                  : "opacity-0 group-hover:opacity-100",
                              )}
                            />
                            <div
                              className={cn(
                                "absolute left-0 top-0 bg-gradient-to-r from-[#F2F5F4] to-transparent w-[50%] h-full",
                                isActive
                                  ? "opacity-100"
                                  : "opacity-0 group-hover:opacity-100",
                              )}
                            />

                            {item.icon && (
                              <div className="relative z-[1] overflow-hidden flex-shrink-0 w-[3rem] h-[3rem]">
                                <Image
                                  src={item.icon}
                                  alt=""
                                  className={cn(
                                    "!w-[3rem] !h-[3rem]",
                                    isActive
                                      ? ""
                                      : " grayscale group-hover:grayscale-0",
                                  )}
                                />
                              </div>
                            )}
                            <div className="relative z-[1] flex flex-col gap-[1rem]">
                              <div className="overflow-hidden relative h-[3.25rem]">
                                <span className="block m-0 text-black font-[500] text-[2.25rem] font-geist ">
                                  {resolvedTitle}
                                </span>
                              </div>
                              <span className="text-[2rem] font-normal leading-[130%] transition-all duration-200 text-black/80 group-hover:text-black/80">
                                {resolvedDescription}
                              </span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                </div>
              )}

              {/* imageSubItems */}
              {col.columnType === "imageSubItems" && (
                <div className="grid grid-cols-1 w-full gap-[2rem] h-full">
                  {col.imageSubItems &&
                    col.imageSubItems.map((item, index) => {
                      const link = resolvePageLink(item.link, language) ?? "/";
                      const resolvedImgTitle = resolveI18nText(
                        item.title,
                        item.i18n,
                        language,
                      );
                      const resolvedImgDescription = resolveI18nText(
                        item.description,
                        item.descriptionI18n,
                        language,
                      );
                      const resolvedMoreInfo = resolveI18nText(
                        item.moreInfo,
                        item.moreInfoI18n,
                        language,
                      );
                      const lang = language as "de" | "br" | undefined;
                      const resolvedImage =
                        (lang && item.imageI18n?.[lang]) || item.image;
                      return (
                        <Link
                          href={link}
                          key={index}
                          className="flex w-full h-full gap-[4rem]"
                        >
                          {resolvedImage && (
                            <GlareHover className="!w-auto !h-[42.72225rem] flex-shrink-0">
                              <Image
                                src={resolvedImage}
                                alt=""
                                className="object-cover !w-full !h-full"
                              />
                            </GlareHover>
                          )}

                          <div className="flex flex-col justify-end w-full">
                            <div className="flex flex-col gap-[0.5rem] w-full">
                              <p className="m-0 text-black/80 font-[500] text-[2.5rem] font-geist">
                                {resolvedImgTitle}
                              </p>
                              {resolvedImgDescription && (
                                <p className="m-0 text-black/60 text-[2rem] leading-[130%]">
                                  {resolvedImgDescription}
                                </p>
                              )}
                            </div>
                            {resolvedMoreInfo && (
                              <div className="mt-[1.25rem] flex justify-between items-center !w-fit group relative bg-black px-[1.5rem] py-[1rem]">
                                <div className="overflow-hidden relative h-[3.375rem]">
                                  <h5 className="text-initial font-[400] text-[2.25rem] leading-[150%] text-white transition-all duration-300 opacity-100 group-hover:opacity-0 group-hover:-translate-y-[3rem]">
                                    {resolvedMoreInfo} →
                                  </h5>
                                  <h5 className="absolute top-[3rem] font-[400] text-[2.25rem] leading-[150%] text-white transition-all duration-300 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:-translate-y-[3rem]">
                                    {resolvedMoreInfo} →
                                  </h5>
                                </div>
                              </div>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                </div>
              )}

              {/* fullImage */}
              {col.columnType === "fullImage" &&
                col.fullImage &&
                (() => {
                  const imgLink = col.fullImage.link
                    ? (resolvePageLink(col.fullImage.link, language) ??
                      undefined)
                    : undefined;
                  const lang = language as "de" | "br" | undefined;
                  const desktopImg =
                    (lang && col.fullImage.imageI18n?.[lang]) ||
                    col.fullImage.image;
                  const mobileImg =
                    (lang && col.fullImage.mobileImageI18n?.[lang]) ||
                    (col.fullImage.mobileImage ?? col.fullImage.image);
                  const content = (
                    <>
                      {mobileImg && (
                        <Image
                          src={mobileImg}
                          alt=""
                          className="lg:hidden !w-full !h-full object-cover"
                        />
                      )}
                      {desktopImg && (
                        <Image
                          src={desktopImg}
                          alt=""
                          className="hidden lg:block !w-full max-h-[42.5rem] object-contain self-end"
                        />
                      )}
                    </>
                  );
                  return imgLink ? (
                    <Link
                      href={imgLink}
                      className={cn(
                        "w-full flex flex-col items-end",
                        !resolvedColumnTitle && "h-full",
                      )}
                    >
                      {content}
                    </Link>
                  ) : (
                    <div
                      className={cn(
                        "w-full flex flex-col justify-end",
                        !resolvedColumnTitle && "h-full",
                      )}
                    >
                      {content}
                    </div>
                  );
                })()}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function MenuItem({
  item,
  headerStyle,
  onOpenChange,
  language,
}: {
  item: {
    title: string;
    i18n?: II18nText;
    isCategory: boolean;
    columns?: IHeaderColumn[];
    link?: IPageLink;
  };
  headerStyle: "light" | "dark";
  onOpenChange?: (open: boolean) => void;
  language?: string;
}) {
  const pathnames = useGlobalPathname();
  const link = resolvePageLink(item.link ?? null, language) ?? "/";
  const isActive = pathnames.currentPathname === link && !item.isCategory;
  const LinkWrapper = item.isCategory ? TooltipLink : Link;
  const [isOpen, setIsOpen] = React.useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    if (item.isCategory) {
      e.preventDefault();
      handleOpenChange(!isOpen);
    }
  };

  const handleMouseEnter = () => {
    if (!item.isCategory) {
      // Close any open dropdowns when hovering over non-category items
      onOpenChange?.(false);
    }
  };

  return (
    <TooltipProvider delayDuration={100} skipDelayDuration={60}>
      <Tooltip open={isOpen} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>
          <LinkWrapper
            href={link}
            onClick={handleClick}
            onTouchEnd={handleClick}
            onMouseEnter={handleMouseEnter}
            className={cn(
              "group px-[1.25rem] text-[1.75rem] font-medium leading-none inline-flex items-center whitespace-nowrap transition-colors font-geist relative data-[state=delayed-open]:opacity-100",
              headerStyle === "dark"
                ? isActive
                  ? "text-black"
                  : "text-black/80 hover:text-black/80"
                : isActive
                  ? "text-white"
                  : "text-white/80 hover:text-white/80",
            )}
          >
            <>
              {resolveI18nText(item.title, item.i18n, language)}

              {item.isCategory && (
                <ChevronDown
                  className={cn(
                    "ml-3 !w-[1.25rem] transition-transform",
                    isOpen && "rotate-180",
                    isActive ? "opacity-100" : "opacity-80",
                  )}
                />
              )}

              <span
                className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[.125rem] w-full ${headerStyle === "dark" ? "bg-black" : "bg-[#D9F9B9]"} group-hover:opacity-100 transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`}
              />
            </>
          </LinkWrapper>
        </TooltipTrigger>
        {item?.isCategory && item?.columns && item.columns?.length > 0 && (
          <TooltipCustomContent
            className="left-0 right-0 !translate-x-0 bg-white border-t border-black/10"
            align="start"
            sideOffset={0}
            alignOffset={-9999}
            onPointerDownOutside={() => handleOpenChange(false)}
          >
            <MenuItemPopover
              item={item}
              currentPath={pathnames.currentPathname}
              headerStyle={headerStyle}
              language={language}
            />
          </TooltipCustomContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

export default function HeaderV2({
  data,
  headerType,
  headerStyle = "dark",
  headerLines = "dark",
  className,
  language,
}: {
  data: IHeader;
  headerType: "main" | "lp" | "hidden";
  headerStyle?: "light" | "dark";
  headerLines?: "light" | "medium" | "dark";
  className?: string;
  language?: string;
}) {
  const pathnames = useGlobalPathname();
  const menuItemsContainer = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileCategory, setActiveMobileCategory] = useState<
    number | null
  >(null);
  const [mobileMenuContainer, setMobileMenuContainer] =
    useState<HTMLElement | null>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    setLoaded(true);

    return () => {
      setLoaded(false);
    };
  }, []);

  // Compute active styles based on dropdown state
  const activeHeaderStyle = "dark"; //isDropdownOpen && headerStyle === "light" ? "dark" : headerStyle;
  const activeHeaderLines =
    isDropdownOpen && headerLines === "light" ? "dark" : headerLines;
  const shouldShowLines = isDropdownOpen;

  useEffect(() => {
    setIsMounted(true);
    if (typeof document !== "undefined") {
      const portal = document.createElement("div");
      portal.id = "mobile-menu";
      portal.style.position = "fixed";
      portal.style.top = "0";
      portal.style.left = "0";
      portal.style.width = "100%";
      portal.style.height = "100%";
      portal.style.zIndex = "100";
      portal.style.transform = "translateX(100%)";
      document.body.appendChild(portal);
      setMobileMenuContainer(portal);
    }

    return () => {
      setIsMounted(false);
      const portal = document.getElementById("mobile-menu");
      if (portal) {
        portal.remove();
      }
      setMobileMenuContainer(null);
    };
  }, []);

  const homeHref = resolveLink(
    language && language !== DEFAULT_LANGUAGE ? `/${language}` : "/",
    language || DEFAULT_LANGUAGE
  );

  const [finalHomeLink, setFinalHomeLink] = useState("");

  useEffect(() => {
    if (!homeHref) return;
    setFinalHomeLink(homeHref);
  }, [homeHref]);

  if (!loaded || !data) {
    return null;
  }

  return (
    <>
      {/* Backdrop blur overlay */}
      <div
        className={cn(
          "fixed inset-0 z-40 transition-all duration-150 top-[8rem]",
          isDropdownOpen
            ? "backdrop-blur-sm bg-black/20 opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
        )}
        onClick={() => setIsDropdownOpen(false)}
      />

      <motion.header
        initial={{ opacity: 0, y: "calc(-100% - 1.5rem)" }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: ease }}
        className={cn(
          "w-full relative lg:px-[7rem] z-50 transition-colors duration-150",
          className,
          isDropdownOpen && "!bg-white",
        )}
      >
        <div
          className={`headerContainer w-full max-w-[172.5rem] h-[8.375rem] mx-auto px-[2rem] lg:px-0 flex items-center gap-[2rem] ${headerType === "main" ? "justify-between lg:justify-center  lg:grid lg:grid-cols-[auto_1fr_auto] " : "justify-center"} `}
        >
          {headerType === "main" && (
            <>
              <div className="flex items-center h-full shrink-0 gap-[1rem] lg:gap-[2rem] lg:justify-start">
                <Link href={finalHomeLink} className="flex-shrink-0">
                  <IQCLogo fill="black" width="17rem" height="3.15rem" />
                </Link>
                <LiveStreamPill />
              </div>
              <div
                className="hidden relative flex-shrink-0 justify-start h-full lg:flex lg:justify-end flex-1"
                ref={menuItemsContainer}
              >
                {data.menu &&
                  data.menu.map((item, index) => (
                    <MenuItem
                      item={item}
                      headerStyle={activeHeaderStyle}
                      key={index}
                      language={language}
                      onOpenChange={(open) => {
                        if (item.isCategory) {
                          setIsDropdownOpen(open);
                        }
                      }}
                    />
                  ))}
              </div>
              <div className="hidden lg:flex items-center gap-[0.8rem]">
                {data?.headerDiscordCTA && (
                  <CTAButton data={data.headerDiscordCTA} />
                )}
                <div className="w-[0.125rem] h-[3rem] shrink-0 bg-black/10" />
                {data?.headerLoginCTA && (
                  <CTAButton data={data.headerLoginCTA} />
                )}
                {data?.headerCTA && <CTAButton data={data.headerCTA} />}
                <LanguageSwitcher language={language} />
              </div>

              <button
                className={cn(
                  "lg:hidden flex-shrink-0 relative",
                  headerStyle === "dark"
                    ? "w-[6.5rem] h-[5rem] flex items-center justify-center rounded-[8px] border-[2px] border-white after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-[inherit] after:pointer-events-none"
                    : "",
                )}
                onClick={() => {
                  setIsMobileMenuOpen(true);
                }}
              >
                <IconMobileMenuCLosed />
              </button>
            </>
          )}

          {headerType === "lp" && (
            <>
              <div className="flex justify-center items-center w-full">
                <Link href={finalHomeLink} className="flex-shrink-0">
                  <IQCLogo fill="black" />
                </Link>
              </div>
            </>
          )}
        </div>

        {mobileMenuContainer &&
          createPortal(
            <>
              <div
                className={`fixed lg:hidden z-[80] w-full h-full transition-all duration-300 ease-out bg-white overflow-hidden flex flex-col ${isMobileMenuOpen ? "-translate-x-full" : "translate-x-0"}`}
              >
                <div className="container flex flex-row justify-between items-center mb-[2rem] px-[2rem] pt-[2.5rem]">
                  <Link href={finalHomeLink} className="flex-shrink-0">
                    <IQCLogo fill="black" width="17rem" height="3.15rem" />
                  </Link>

                  <button
                    className="relative flex items-center justify-center  border-white "
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setActiveMobileCategory(null);
                    }}
                  >
                    <div className="w-[4rem] h-[4rem]">
                      <IconMobileMenuOpen />
                    </div>
                  </button>
                </div>

                <div className="flex items-center gap-[0.8rem] justify-between px-[2.75rem]">
                  <LanguageSwitcher language={language} />
                  {data?.headerDiscordCTA && (
                    <CTAButton data={data.headerDiscordCTA} className="w-fit" />
                  )}
                </div>

                {/* Two-panel sliding nav */}
                <div className="flex-1 overflow-hidden">
                  <div
                    className="flex w-[200%] h-full transition-transform duration-300 ease-out"
                    style={{
                      transform:
                        activeMobileCategory !== null
                          ? "translateX(-50%)"
                          : "translateX(0)",
                    }}
                  >
                    {/* Panel 1: Top-level items */}
                    <div className="w-1/2 flex flex-col overflow-y-auto px-[3rem]">
                      {data.menu &&
                        data.menu.map((item, index) => {
                          const link =
                            resolvePageLink(item.link ?? null, language) ?? "/";
                          const isActive = pathnames.currentPathname === link;
                          return (
                            <React.Fragment key={index}>
                              {item.isCategory ? (
                                <button
                                  onClick={() => setActiveMobileCategory(index)}
                                  className="w-full py-[3rem] flex justify-between items-center"
                                >
                                  <span className="text-[2.5rem] leading-none font-[500] font-geist text-black/80">
                                    {resolveI18nText(
                                      item.title,
                                      item.i18n,
                                      language,
                                    )}
                                  </span>
                                  <ChevronRight className="!w-[2.5rem] shrink-0 text-black/40" />
                                </button>
                              ) : (
                                <Link
                                  className="w-full py-[3rem] flex justify-between items-center"
                                  href={link}
                                  target={
                                    item.link?.externalLink ? "_blank" : "_self"
                                  }
                                >
                                  <span
                                    className={`text-[2.5rem] leading-none font-[500] font-geist ${isActive ? "text-black/80" : "text-black/80"}`}
                                  >
                                    {resolveI18nText(
                                      item.title,
                                      item.i18n,
                                      language,
                                    )}
                                  </span>
                                </Link>
                              )}
                            </React.Fragment>
                          );
                        })}

                      <div className="mt-auto flex flex-col w-full gap-[1rem] pb-[4rem] pt-[2rem]">
                        {data?.headerLoginCTA && (
                          <CTAButton data={data.headerLoginCTA} />
                        )}
                        {data?.headerCTA && <CTAButton data={data.headerCTA} />}
                      </div>
                    </div>

                    {/* Panel 2: Sub-items for selected category */}
                    <div className="w-1/2 flex flex-col overflow-y-auto h-full">
                      {(() => {
                        const activeItem =
                          activeMobileCategory !== null
                            ? data.menu?.[activeMobileCategory]
                            : null;
                        if (!activeItem) return null;
                        return (
                          <>
                            {/* Back header */}
                            <div className="px-[3rem]">
                              <button
                                onClick={() => setActiveMobileCategory(null)}
                                className="flex items-center gap-[1.2rem] py-[3rem]"
                              >
                                <ArrowLeft className="w-[2rem] shrink-0 text-black/50" />
                                <span className="text-[2.5rem] font-[500] font-geist text-black">
                                  {resolveI18nText(
                                    activeItem.title,
                                    activeItem.i18n,
                                    language,
                                  )}
                                </span>
                              </button>
                            </div>

                            {/* Columns */}
                            <div className="px-[3rem] flex flex-col gap-[2rem] pt-[2rem] pb-[4rem] h-full">
                              {activeItem.columns?.map((col, colIndex) => {
                                if (col.columnType === "none") return null;
                                const resolvedTitle = resolveI18nText(
                                  col.columnTitle,
                                  col.columnTitleI18n,
                                  language,
                                );
                                return (
                                  <div
                                    key={col._key ?? colIndex}
                                    className="flex flex-col gap-[1rem]"
                                  >
                                    {resolvedTitle && (
                                      <div className="border-b border-black/10 pb-[0.8rem]">
                                        <h5 className="text-[1.75rem] font-geist uppercase leading-[100%] text-black/30">
                                          {resolvedTitle}
                                        </h5>
                                      </div>
                                    )}

                                    {/* subItems */}
                                    {col.columnType === "subItems" &&
                                      col.subItems?.map((subItem, idx) => {
                                        const subLink =
                                          resolvePageLink(
                                            subItem?.link,
                                            language,
                                          ) ?? "/";
                                        const isSubActive =
                                          pathnames.currentPathname === subLink;
                                        const isLast =
                                          idx ===
                                          (col.subItems?.length ?? 0) - 1;
                                        return (
                                          <Fragment key={`submenu-${idx}`}>
                                            <Link
                                              href={subLink}
                                              aria-current={
                                                isSubActive ? "page" : undefined
                                              }
                                              className={cn(
                                                "border-l border-transparent transition-colors",
                                                isSubActive
                                                  ? "pointer-events-none border-[#228E71]"
                                                  : "hover:border-[#228E71]",
                                              )}
                                            >
                                              <div
                                                className={cn(
                                                  "flex items-start gap-[1.6rem] rounded-[.5rem] p-[1rem] transition-colors",
                                                  isSubActive
                                                    ? "bg-[#F2F5F4]"
                                                    : "hover:bg-[#F2F5F4]",
                                                )}
                                              >
                                                {subItem.icon && (
                                                  <Image
                                                    src={subItem.icon}
                                                    alt=""
                                                    className="flex-shrink-0 !w-[3rem]"
                                                  />
                                                )}
                                                <div className="flex flex-col gap-[0.5rem]">
                                                  <span className="text-[2.25rem] font-[500] font-geist text-black/80">
                                                    {resolveI18nText(
                                                      subItem.title,
                                                      subItem.i18n,
                                                      language,
                                                    )}
                                                  </span>
                                                  <p className="text-black/50 text-[2rem] leading-[130%]">
                                                    {resolveI18nText(
                                                      subItem.description,
                                                      subItem.descriptionI18n,
                                                      language,
                                                    )}
                                                  </p>
                                                </div>
                                              </div>
                                            </Link>
                                          </Fragment>
                                        );
                                      })}

                                    {/* imageSubItems */}
                                    {col.columnType === "imageSubItems" &&
                                      col.imageSubItems?.map((imgItem, idx) => {
                                        const imgLink =
                                          resolvePageLink(
                                            imgItem?.link,
                                            language,
                                          ) ?? "/";
                                        const imgLang = language as
                                          | "de"
                                          | "br"
                                          | undefined;
                                        const resolvedMobileImage =
                                          (imgLang &&
                                            imgItem.imageI18n?.[imgLang]) ||
                                          imgItem.image;
                                        return (
                                          <Fragment key={`imgsubmenu-${idx}`}>
                                            <Link
                                              href={imgLink}
                                              className="flex items-stretch gap-[1rem]"
                                            >
                                              {resolvedMobileImage && (
                                                <Image
                                                  src={resolvedMobileImage}
                                                  alt=""
                                                  className="object-cover flex-shrink-0 !w-[12rem] aspect-square"
                                                />
                                              )}
                                              <div className="flex flex-col justify-between w-full p-[1rem]">
                                                <div className="flex flex-col gap-[0.75rem]">
                                                  <p className="m-0 text-black/80 font-[500] text-[2.5rem] font-geist">
                                                    {resolveI18nText(
                                                      imgItem.title,
                                                      imgItem.i18n,
                                                      language,
                                                    )}
                                                  </p>
                                                  {resolveI18nText(
                                                    imgItem.description,
                                                    imgItem.descriptionI18n,
                                                    language,
                                                  ) && (
                                                    <p className="m-0 text-black/60 text-[2rem] leading-[130%]">
                                                      {resolveI18nText(
                                                        imgItem.description,
                                                        imgItem.descriptionI18n,
                                                        language,
                                                      )}
                                                    </p>
                                                  )}
                                                </div>
                                                {resolveI18nText(
                                                  imgItem.moreInfo,
                                                  imgItem.moreInfoI18n,
                                                  language,
                                                ) && (
                                                  <div className="mt-[1.25rem] flex justify-between items-center !w-fit group relative bg-black px-[1.5rem] py-[1rem]">
                                                    <div className="overflow-hidden relative h-[3rem]">
                                                      <h5 className="text-initial font-[400] text-[2rem] leading-[150%] text-white transition-all duration-300 opacity-100 group-hover:opacity-0 group-hover:-translate-y-[3rem]">
                                                        {resolveI18nText(
                                                          imgItem.moreInfo,
                                                          imgItem.moreInfoI18n,
                                                          language,
                                                        )}{" "}
                                                        →
                                                      </h5>
                                                      <h5 className="absolute top-[3rem] font-[400] text-[2rem] leading-[150%] text-white transition-all duration-300 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:-translate-y-[3rem]">
                                                        {resolveI18nText(
                                                          imgItem.moreInfo,
                                                          imgItem.moreInfoI18n,
                                                          language,
                                                        )}{" "}
                                                        →
                                                      </h5>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </Link>
                                          </Fragment>
                                        );
                                      })}

                                    {/* fullImage */}
                                    {col.columnType === "fullImage" &&
                                      col.fullImage &&
                                      (() => {
                                        const fImgLink = col.fullImage.link
                                          ? (resolvePageLink(
                                              col.fullImage.link,
                                              language,
                                            ) ?? undefined)
                                          : undefined;
                                        const lang = language as
                                          | "de"
                                          | "br"
                                          | undefined;
                                        const mobileImg =
                                          (lang &&
                                            col.fullImage.mobileImageI18n?.[
                                              lang
                                            ]) ||
                                          (col.fullImage.mobileImage ??
                                            col.fullImage.image);
                                        const imgEl = mobileImg ? (
                                          <Image
                                            src={mobileImg}
                                            alt=""
                                            className="!w-full object-cover"
                                          />
                                        ) : null;
                                        return fImgLink ? (
                                          <Link
                                            href={fImgLink}
                                            className="block w-full"
                                          >
                                            {imgEl}
                                          </Link>
                                        ) : (
                                          <div className="w-full">{imgEl}</div>
                                        );
                                      })()}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </>,
            mobileMenuContainer,
            "mobile-menu",
          )}

        {headerType !== "lp" && (
          <DecorationLine
            className={cn(
              `headerHorizontalLine bottom-0 left-0 transition-opacity duration-150`,
              "border-b-[#D9D9D9] border-t-0",
              shouldShowLines ? "!opacity-100" : "",
            )}
            orientation="horizontal"
            style={activeHeaderLines === "dark" ? "dark" : "light"}
          />
        )}
      </motion.header>
    </>
  );
}
